import type { SimpleRequestConfig } from 'modules/app/types';
import type { ApiError, EmptyResponseBody, MetaAction } from 'modules/global/generic.types';

import * as actions from 'modules/guidelines/actions';
import * as types from 'modules/guidelines/actionTypes';
import type { DeleteGuidelineAction, PreviewGuidelineAction } from 'modules/guidelines/types/actions';

// PUT, PATCH and brand assignment all surface the same "updated" notification.
const UPDATE_NOTIFICATIONS = {
  success: { id: 'page.fiche.guidelines.notifications.put.success', defaultMessage: 'Guideline updated' },
  failure: { id: 'page.fiche.guidelines.notifications.put.failure', defaultMessage: "Can't update the guideline" },
};

/**
 * Declarative request handling (request + success/failure dispatch + notifications).
 * For each entry, `simpleRequest` auto-derives the `_SUCCESS`/`_FAILURE` action
 * (payload = response) unless a callback overrides it. The page-specific reactions to
 * these successes (create/clone redirect, assignment modal) live scoped in
 * `details.ts` / `dashboard.ts`; the global list pagination stays manual in `index.ts`.
 */
const requestsConfig: Record<string, SimpleRequestConfig> = {
  [types.PREVIEW_GUIDELINE]: {
    callbacks: {
      // Re-attach the targeted guideline/item (carried by the request payload) so the
      // reducers can key on them without digging into meta.
      success: (previews: Record<string, string>, meta: MetaAction, { payload }: PreviewGuidelineAction) =>
        actions.previewGuidelineSuccess({ guidelineId: payload.guidelineId, itemId: payload.itemId, previews }, meta),
      failure: (error: ApiError, meta: MetaAction, { payload }: PreviewGuidelineAction) =>
        actions.previewGuidelineFailure({ guidelineId: payload.guidelineId, itemId: payload.itemId, error }, meta),
    },
    notifications: { success: () => undefined, failure: () => undefined },
  },
  [types.FETCH_GUIDELINE]: {
    notifications: { failure: () => undefined },
  },
  [types.POST_GUIDELINE]: {
    notifications: {
      success: { id: 'page.fiche.guidelines.notifications.create.success', defaultMessage: 'Guideline created' },
      failure: { id: 'page.fiche.guidelines.notifications.create.failure', defaultMessage: "Can't create the guideline" },
    },
  },
  [types.POST_CLONE_GUIDELINE]: {
    notifications: {
      success: { id: 'page.fiche.guidelines.notifications.clone.success', defaultMessage: 'Guideline cloned' },
      failure: { id: 'page.fiche.guidelines.notifications.clone.failure', defaultMessage: "Can't clone the guideline" },
    },
  },
  [types.PUT_GUIDELINE]: { notifications: UPDATE_NOTIFICATIONS },
  [types.ASSIGN_BRANDS_TO_GUIDELINE]: { notifications: UPDATE_NOTIFICATIONS },
  [types.DELETE_GUIDELINE]: {
    callbacks: {
      // The reducer keys on the functional guidelineId, not the (empty) 204 body.
      success: (_data: EmptyResponseBody, meta: MetaAction, { payload }: DeleteGuidelineAction) =>
        actions.deleteGuidelineSuccess(payload.guidelineId, meta),
    },
    notifications: {
      success: { id: 'page.fiche.guidelines.notifications.delete.success', defaultMessage: 'Guideline deleted' },
      failure: { id: 'page.fiche.guidelines.notifications.delete.failure', defaultMessage: "Can't delete the guideline" },
    },
  },
};

export default requestsConfig;
